import { computed, type ComputedRef, onBeforeUnmount, onDeactivated, ref, type Ref } from "vue";
import { Temporal } from "temporal-polyfill";

interface AutoDismissBase {
    /** Start the dismiss timer with the given duration. */
    start: (duration: Temporal.Duration) => void;
    /** Restart the timer using the `defaultDuration` option. No-op if `defaultDuration` was not provided. */
    restart: () => void;
    /** Abort the current timer and reset state. */
    reset: () => void;
    /** Whether a dismiss timer is currently active. */
    isRunning: Ref<boolean>;
}

interface AutoDismissWithProgress extends AutoDismissBase {
    /** Current progress as a value from 0 to 1. */
    progress: Ref<number>;
    /** Estimated whole seconds remaining until the timer completes. */
    remainingSeconds: ComputedRef<number>;
    /** Whether progress has reached 1 (the timer has completed). */
    isComplete: ComputedRef<boolean>;
    /** Interval in milliseconds between progress ticks. */
    tickRateMs: number;
}

/**
 * Composable that handles auto-dismisses after a duration, optionally tracking progress.
 *
 * When `trackProgress` is `true`, returns reactive `progress`, `remainingSeconds`, and `isComplete`
 * properties updated every 500 ms. When `false` (or omitted), a simple `setTimeout` fires the
 * callback with no intermediate updates.
 *
 * This composable automatically cleans up its active timers on its own through the events `onBeforeUnmount` and `onDeactivated`.
 *
 * @param callback - Optional function to invoke when the timer completes (e.g. closing a modal).
 * @param options.trackProgress - Enable interval-based progress tracking.
 * @param options.defaultDuration - Duration used by {@link AutoDismissBase.restart | reset()}.
 */
export function useAutoDismiss(callback?: () => void | Promise<void>, options?: { trackProgress: true; defaultDuration?: Temporal.DurationLike }): AutoDismissWithProgress;
/** @inheritDoc */
export function useAutoDismiss(callback?: () => void | Promise<void>, options?: { trackProgress: false; defaultDuration?: Temporal.DurationLike }): AutoDismissBase;
export function useAutoDismiss(callback?: () => void | Promise<void>, options?: { trackProgress: boolean; defaultDuration?: Temporal.DurationLike }): AutoDismissBase | AutoDismissWithProgress {
    const trackProgress = options?.trackProgress;

    const progress = ref(0);
    const isRunning = ref(false);
    let timerId: ReturnType<typeof setInterval> | ReturnType<typeof setTimeout> | null = null;
    let totalMs = 0;
    const TICK_MS = 500;

    const remainingSeconds = computed(() => {
        if (totalMs === 0) return 0;
        return Math.max(Math.ceil((totalMs * (1 - progress.value)) / 1000), 0);
    });

    const isComplete = computed(() => progress.value >= 1);

    function clearTimer(): void {
        if (timerId !== null) {
            if (trackProgress) {
                clearInterval(timerId);
            } else {
                clearTimeout(timerId);
            }
            timerId = null;
        }
    }

    async function onComplete(): Promise<void> {
        clearTimer();
        isRunning.value = false;
        try {
            await callback?.();
        } catch (e) {
            console.error("useAutoDismiss callback failed", e);
        }
    }

    function start(duration: Temporal.Duration): void {
        reset();

        totalMs = duration.total({ unit: "milliseconds" });
        isRunning.value = true;

        if (trackProgress) {
            const startTime = Temporal.Now.instant();
            timerId = setInterval(async () => {
                const elapsed = Temporal.Now.instant().since(startTime).total({ unit: "milliseconds" });
                progress.value = Math.min(elapsed / totalMs, 1);

                if (progress.value === 1) {
                    onComplete();
                }
            }, TICK_MS);
        } else {
            timerId = setTimeout(onComplete, totalMs);
        }
    }

    function reset(): void {
        clearTimer();
        isRunning.value = false;
        progress.value = 0;
    }

    function restart(): void {
        if (options?.defaultDuration == null) return;
        start(Temporal.Duration.from(options.defaultDuration));
    }

    onBeforeUnmount(() => reset());
    onDeactivated(() => reset());

    if (trackProgress) {
        return { progress, remainingSeconds, isComplete, isRunning, start, restart, reset, tickRateMs: TICK_MS };
    }

    return { start, restart, reset, isRunning };
}
